package com.exura.features.plandefinition.domain.builder.plan

import com.exura.features.plandefinition.domain.builder.BuilderField
import com.exura.features.plandefinition.domain.builder.PlanElementBuilder
import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.BaseSeries

/**
 * Enhanced Base Plan Builder with proper state tracking and collection/series management
 */
abstract class BasePlanBuilderNew<T : BasePlan>(
    val planType: PlanType,
) : PlanElementBuilder<T> {

    private val firebaseDocId = BuilderField<String?>()
    private val title = BuilderField<String>(isRequired = true) {title ->
        title.isNotEmpty()
    }
    private val description = BuilderField<String>(isRequired = true, default = "")
    private val imageUrl = BuilderField<String?>()
    private val collections = BuilderField<MutableList<Collection>>(
        value = mutableListOf()
    )
    private val directSeries = BuilderField<MutableList<BaseSeries<out BasePlanElement>>>(
        value = mutableListOf()
    )

    override var isChanged: Boolean = false
    override var buildResultNeedRecalculation: Boolean = true
    override var cachedBuildResult: T? = null

    // Getters
    fun firebaseDocId(): String? = firebaseDocId.getValue()
    fun title(): String? = title.getValue()
    fun description(): String? = description.getValue()
    fun imageUrl(): String? = imageUrl.getValue()
    fun collections(): List<Collection> = collections.getValue()?.toList() ?: emptyList()
    fun directSeries(): List<BaseSeries<out BasePlanElement>> = directSeries.getValue()?.toList() ?: emptyList()
    fun setFirebaseDocId(newValue: String?) = apply {
        val res = firebaseDocId.setValue(newValue)
        updateChangedState(res)
    }

    fun setTitle(newValue: String) = apply {
        val res = title.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
    }

    fun setDescription(newValue: String) = apply {
        val res = description.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
    }

    fun setImageUrl(newValue: String?) = apply {
        val res = imageUrl.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
    }

    fun addCollection(newCollection: Collection?) = apply {
        val currentList = collections.getValue()!!

        if (newCollection != null) {
            currentList.add(newCollection)
            collections.markChanged()
            updateChangedState(true)
            updateBuildState(true)
        }
    }

    fun updateCollection(collectionIndex: Int, newCollection: Collection?) = apply {
        val currentList = collections.getValue()!!

        if (newCollection != null && collectionIndex in currentList.indices) {
            val existingCollection = currentList[collectionIndex]

            // Only update if the collection is actually different
            if (existingCollection != newCollection) {
                currentList[collectionIndex] = newCollection
                collections.markChanged()
                updateChangedState(true)
                updateBuildState(true)
            }
        }
    }

    fun removeCollection(collectionIndex: Int) = apply {
        val currentList = collections.getValue()!!

        if (collectionIndex in currentList.indices) {
            currentList.removeAt(collectionIndex)
            collections.markChanged()
            updateChangedState(true)
            updateBuildState(true)
        }
    }

    fun editCollection(collectionIndex: Int, editor: (Collection) -> Collection?) = apply {
        val currentList = collections.getValue()!!

        if (collectionIndex in currentList.indices) {
            val existingCollection = currentList[collectionIndex]
            val editedCollection = editor(existingCollection)

            // Only update if the collection actually changed
            if (existingCollection != editedCollection && editedCollection != null) {
                currentList[collectionIndex] = editedCollection
                collections.markChanged()
                updateChangedState(true)
                updateBuildState(true)
            }
        }
    }

    fun addCollection(factory: () -> Collection?) = apply {
        val newCollection = factory()
        addCollection(newCollection)
    }

    fun addDirectSeries(newSeries: BaseSeries<out BasePlanElement>?) = apply {
        val currentList = directSeries.getValue()!!

        if (newSeries != null) {
            currentList.add(newSeries)
            directSeries.markChanged()
            updateChangedState(true)
            updateBuildState(true)
        }
    }

    fun updateDirectSeries(seriesIndex: Int, newSeries: BaseSeries<out BasePlanElement>?) = apply {
        val currentList = directSeries.getValue()!!
        if (newSeries != null && seriesIndex in currentList.indices) {
            val existingSeries = currentList[seriesIndex]
            if (existingSeries != newSeries) {
                currentList[seriesIndex] = newSeries
                directSeries.markChanged()
                updateChangedState(true)
                updateBuildState(true)
            }
        }
    }

    fun removeDirectSeries(seriesIndex: Int) = apply {
        val currentList = directSeries.getValue()!!

        if (seriesIndex in currentList.indices) {
            currentList.removeAt(seriesIndex)
            directSeries.markChanged()
            updateChangedState(true)
            updateBuildState(true)
        }
    }

    fun <S : BaseSeries<out BasePlanElement>> editDirectSeries(
        seriesIndex: Int,
        editor: (S) -> S?
    ) = apply {
        val currentList = directSeries.getValue()!!

        if (seriesIndex in currentList.indices) {
            @Suppress("UNCHECKED_CAST")
            val existingSeries = currentList[seriesIndex] as S
            val editedSeries = editor(existingSeries)

            // Only update if the series actually changed
            if (existingSeries != editedSeries && editedSeries != null) {
                currentList[seriesIndex] = editedSeries
                directSeries.markChanged()
                updateChangedState(true)
                updateBuildState(true)
            }
        }
    }

    fun addDirectSeries(factory: () -> BaseSeries<out BasePlanElement>?) = apply {
        val newSeries = factory()
        addDirectSeries(newSeries)
    }

    fun addAllCollections(newCollections: List<Collection>) = apply {
        newCollections.forEach { addCollection(it) }
    }

    fun setCollections(newCollections: List<Collection>) = apply {
        val currentList = collections.getValue() ?: mutableListOf()
        currentList.clear()
        currentList.addAll(newCollections)
        val res = collections.setValue(currentList)
        updateChangedState(res)
        updateBuildState(res)
    }

    fun addAllDirectSeries(newSeries: List<BaseSeries<out BasePlanElement>>) = apply {
        newSeries.forEach { addDirectSeries(it) }
    }

    fun setDirectSeries(newSeries: List<BaseSeries<out BasePlanElement>>) = apply {
        val currentList = directSeries.getValue() ?: mutableListOf()
        currentList.clear()
        currentList.addAll(newSeries)
        val res = directSeries.setValue(currentList)
        updateChangedState(res)
        updateBuildState(res)
    }

    override fun validate(): List<String> {
        val fields = mapOf(
            "firebaseDocId" to firebaseDocId,
            "title" to title,
            "description" to description,
            "imageUrl" to imageUrl,
            "collections" to collections,
            "directSeries" to directSeries,
        )
        val messages = validateFields(fields)
        return messages
    }

    override fun markSaved() = apply {
        firebaseDocId.markSaved()
        title.markSaved()
        description.markSaved()
        imageUrl.markSaved()
        collections.markSaved()
        directSeries.markSaved()
        super.markSaved()
    }
    abstract override fun build(): T

//
//    // Helper method for creating builders from existing plans
//    protected fun populateFromPlan(plan: BasePlan) = apply {
//        setFirebaseDocId(plan.firebaseDocId)
//        setTitle(plan.title)
//        setDescription(plan.description)
//        setImageUrl(plan.imageUrl)
//        setCollections(plan.collections)
//        setDirectSeries(plan.directSeries)
//        markSaved()
//        isChanged = false
//    }
}